import { FC, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import Button from 'components/button';
import InputSelect from 'components/inputs/inputSelect';
import Tooltip from 'components/tooltip';
import { Input, Label, ModalContent, ModalSection, Textarea } from 'components/modal/styles';
import { SsoProvider } from 'contexts/accounts/types';
import {
  COMPANY_NAME_LOCKED_MSG,
  FormReducer,
  METADATA_FIELDS,
  METADATA_FILE_HELP,
  SsoDataState,
  SsoFormFields,
  SsoInputMode,
  getLoginUrlExample,
  getLoginUrlHint,
} from './helper';
import { IAccountSsoProviderError } from './helper';

const CustomModalSection = styled(ModalSection)`
  margin-top: 0;
  margin-bottom: 20px;
`;

interface ISsoProviderFormProps {
  errors: IAccountSsoProviderError[];
  formData: SsoDataState;
  loading: boolean;
  ssoProvider?: SsoProvider;
  inputMode?: SsoInputMode;
  metadataFileName?: string;
  metadataError?: string;
  onMetadataFile?(file?: File): void;
  onFieldChange(data: FormReducer): void;
  onCancel(): void;
  onSubmit(): void;
}

const SsoProviderForm: FC<ISsoProviderFormProps> = ({
  errors,
  formData,
  loading,
  ssoProvider,
  inputMode = 'manual',
  metadataFileName,
  metadataError,
  onMetadataFile,
  onFieldChange,
  onCancel,
  onSubmit,
}) => {
  const loginUrlExample = getLoginUrlExample(formData.providerName);
  const metadataInput = useRef<HTMLInputElement>(null);

  const getHint = (field: typeof SsoFormFields[number], locked: boolean) => {
    if (locked) return COMPANY_NAME_LOCKED_MSG;
    if (field.name === 'loginUrl') return getLoginUrlHint(formData.providerName);
    return field.hint;
  };

  return (
    <ModalContent style={{ padding: '0px' }}>
      {inputMode === 'upload' && (
        <CustomModalSection>
          <div className="d-grid">
            <Label htmlFor="sso-metadata-file">Metadata XML file *</Label>
            <div className="d-flex align-items-center" style={{ gap: '12px' }}>
              <Button
                $secondary
                icon={'Upload'}
                aria-label="Choose metadata XML file"
                onClick={() => metadataInput.current?.click()}
              >
                Choose file
              </Button>
              {metadataFileName && <small>{metadataFileName}</small>}
            </div>
            <input
              ref={metadataInput}
              id="sso-metadata-file"
              type="file"
              style={{ display: 'none' }}
              accept=".xml,application/xml,text/xml"
              onChange={(e) => {
                onMetadataFile?.(e.currentTarget.files?.[0]);
                // Clear it so choosing the same (fixed) file again fires another change.
                e.currentTarget.value = '';
              }}
            />
            {metadataError && <small style={{ color: 'red' }}>{metadataError}</small>}
            {!metadataError && metadataFileName && (
              <small>Login URL, certificate and issuer filled from {metadataFileName}.</small>
            )}
            <small className="mt-2">Download it from your identity provider:</small>
            <ul className="mb-0">
              {METADATA_FILE_HELP.map((help) => (
                <li key={help}>
                  <small>{help}</small>
                </li>
              ))}
            </ul>
          </div>
        </CustomModalSection>
      )}
      {SsoFormFields.map((field) => {
        const hasError = errors.some((e) => e.key === field.key);
        // company_name is the Cognito provider name, which can't be renamed.
        const locked = !!ssoProvider && field.name === 'companyName';
        // Filled from the uploaded metadata file instead of typed.
        const fromMetadata = inputMode === 'upload' && METADATA_FIELDS.includes(field.name);
        const hint = getHint(field, locked);

        const input = (
          <Input
            error={hasError}
            placeholder={field.placeholder}
            value={formData[field.name]}
            disabled={locked}
            readOnly={fromMetadata}
            style={locked || fromMetadata ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
            onChange={(e) =>
              onFieldChange({
                type: field.key,
                value: e.currentTarget.value,
              })
            }
          />
        );

        return (
          <CustomModalSection key={field.key}>
            {field.label && <Label>{field.label}</Label>}
            {field.type === 'input' &&
              (locked ? (
                <Tooltip title={COMPANY_NAME_LOCKED_MSG}>
                  {/* antd tooltips don't fire on disabled inputs, so hover the wrapper */}
                  <span style={{ display: 'block' }}>{input}</span>
                </Tooltip>
              ) : (
                input
              ))}
            {field.type === 'select' && (
              <InputSelect
                error={hasError}
                value={formData[field.name]}
                placeHolder={field.placeholder}
                options={field.options}
                maxDropdownHeight={'500px'}
                onChange={(e) =>
                  onFieldChange({
                    type: field.key,
                    value: e,
                  })
                }
              />
            )}
            {field.type === 'textarea' && (
              <Textarea
                error={hasError}
                value={formData[field.name]}
                readOnly={fromMetadata}
                style={fromMetadata ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
                onChange={(e) =>
                  onFieldChange({
                    type: field.key,
                    value: e.currentTarget.value,
                  })
                }
              />
            )}
            <div className="d-grid">
              {hasError && (
                <small style={{ color: 'red' }}>
                  {errors.find((e) => e.key === field.key).msg}
                </small>
              )}
              {hint && <small>{hint}</small>}
              {/* Own line so long example URLs don't wrap mid-word after the hint text */}
              {field.name === 'loginUrl' && loginUrlExample && <small>{loginUrlExample}</small>}
            </div>
          </CustomModalSection>
        );
      })}
      <CustomModalSection className="d-flex">
        <Button
          $secondary
          disabled={loading}
          style={{ margin: isMobile ? 'auto 10px 0 auto' : '0 0 0 auto' }}
          icon={'Cancel2'}
          onClick={onCancel}
        >
          {'Cancel'}
        </Button>
        <Button
          $primary
          disabled={loading}
          style={{ margin: isMobile ? 'auto 10px 0 auto' : '0 0 0 auto' }}
          icon={'Save'}
          onClick={onSubmit}
        >
          {'Save and publish'}
        </Button>
      </CustomModalSection>
    </ModalContent>
  );
};

export default SsoProviderForm;
